import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { getTranslations } from './i18n';

import './SurveyResponse.css';
import '../Platform/system/survey/ui/backgroundStyles.css';
import IntroSurvey from './IntroSurvey';
import ThanksUser from './ThanksUser';
import ThanksUserResult from './ThanksUserResult';
import ProgressBarUser from './ProgressBarUser';
import PlatformFooter from './PlatformFooter';

import { SubmittingOverlay, PreparingResultScreen } from './SurveyStatusScreens';
import { getFormLayout, DEFAULT_SURVEY_TYPE as _DFT } from './SurveyFormLayouts';
import {
  COMMENT_MAX_LENGTH,
  THANKS_DELAY_MS,
  AUTO_ADVANCE_DELAY_MS,
  DEFAULT_SURVEY_TYPE,
  GLASS_SX,
  initializeAnswers,
  isQuestionAnswered,
  useViewportHeight,
} from './surveyHelpers';

// Delay artificial para que el "enviando..." se sienta igual que en el
// formulario real, aunque acá no hay ninguna llamada de red que esperar.
const PREVIEW_SUBMIT_DELAY_MS = 500;

// ─── Preview toolbar ──────────────────────────────────────────────────────────
// Barra fija que identifica el modo preview y da acceso rápido a
// reiniciar el recorrido o salir, sin depender de recargar la página
// (que en el editor no tiene urlId ni Firestore de donde volver a leer).
const PreviewToolbar = ({ onReset, onExit }) => (
  <Box
    sx={{
      position:        'sticky',
      top:              0,
      zIndex:           1300,
      display:          'flex',
      alignItems:       'center',
      justifyContent:   'space-between',
      gap:              1,
      px:               1.5,
      py:               0.75,
      mb:               1.5,
      borderRadius:     2,
      background:       'rgba(25, 118, 210, 0.92)',
      color:            '#fff',
      backdropFilter:   'blur(4px)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <VisibilityIcon fontSize="small" />
      <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
        VISTA PREVIA
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Reiniciar recorrido">
        <IconButton size="small" onClick={onReset} sx={{ color: '#fff' }}>
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onExit && (
        <Tooltip title="Salir de la vista previa">
          <IconButton size="small" onClick={onExit} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
// Diferencias clave respecto a SurveyResponse.jsx:
//   - `survey` llega como prop ya armada (la del borrador que se está
//     editando), no se busca en Firestore por urlId.
//   - No hay `hasAlreadySubmitted`/`markAsSubmitted`: la preview nunca
//     queda "bloqueada" por localStorage.
//   - `handleSubmit` no escribe ningún `writeBatch`: arma el mismo
//     `docData` que se usaría para guardar (para que ThanksUserResult
//     pueda puntuar la respuesta) pero solo lo guarda en estado local.
//   - Se agrega una barra superior de preview con reinicio rápido.
const SurveyPreview = ({ survey, logoUrl = null, onExitPreview }) => {
  const t = useMemo(() => getTranslations(), []);

  useViewportHeight();

  const [answers, setAnswers]                 = useState(() => initializeAnswers(survey?.questions));
  const [comment, setComment]                 = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [error, setError]                     = useState('');
  const [showValidation, setShowValidation]   = useState(false);
  const [submitSuccess, setSubmitSuccess]     = useState(false);
  const [showThanks, setShowThanks]           = useState(false);
  const [preparingThanks, setPreparingThanks] = useState(false);
  const [submittedDocData, setSubmittedDocData] = useState(null);
  const [surveyStarted, setSurveyStarted]     = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const thanksTimeoutRef      = useRef(null);
  const autoAdvanceTimeoutRef = useRef(null);
  const submitTimeoutRef      = useRef(null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const sortedQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return Object.entries(survey.questions).sort(
      ([, a], [, b]) => (a.orden || 0) - (b.orden || 0)
    );
  }, [survey?.questions]);

  const layoutConfig = useMemo(
    () => getFormLayout(survey?.type ?? DEFAULT_SURVEY_TYPE),
    [survey?.type]
  );
  const FormLayout    = layoutConfig.Component;
  const isSteppedFlow = layoutConfig.isStepped;

  const currentQuestionEntry = sortedQuestions[currentStepIndex] || null;
  const isLastQuestion       = currentStepIndex >= sortedQuestions.length - 1;
  const isTextStep           = currentQuestionEntry?.[1]?.type === 'text';

  const showNextOrSubmitButton = !isSteppedFlow || isLastQuestion || isTextStep;

  const progress = useMemo(() => {
    if (!sortedQuestions.length) return 0;
    const answered = sortedQuestions.filter(([qId, q]) =>
      isQuestionAnswered(q, answers[qId])
    ).length;
    return Math.round((answered / sortedQuestions.length) * 100);
  }, [answers, sortedQuestions]);

  const unansweredQuestions = useMemo(() => {
    if (!showValidation || !sortedQuestions.length) return new Set();
    return new Set(
      sortedQuestions
        .filter(([qId, q]) => !isQuestionAnswered(q, answers[qId]))
        .map(([qId]) => qId)
    );
  }, [sortedQuestions, answers, showValidation]);

  // ── Reset completo del recorrido (usado por handleRetake y el botón de
  //    la toolbar). No depende de ningún fetch: `survey` ya está en props.
  const resetPreviewState = useCallback(() => {
    if (thanksTimeoutRef.current) clearTimeout(thanksTimeoutRef.current);
    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);

    setAnswers(initializeAnswers(survey?.questions));
    setComment('');
    setSubmitting(false);
    setError('');
    setShowValidation(false);
    setSubmitSuccess(false);
    setShowThanks(false);
    setPreparingThanks(false);
    setSubmittedDocData(null);
    setSurveyStarted(false);
    setCurrentStepIndex(0);
  }, [survey?.questions]);

  // Reinicia el recorrido cuando cambia el CONJUNTO de preguntas (se
  // agregó/borró alguna), no en cada edición menor (texto, estilos, etc.)
  // — así el admin no pierde su lugar en la preview mientras ajusta
  // detalles que no afectan la estructura de la encuesta.
  const questionIdsKey = useMemo(
    () => Object.keys(survey?.questions || {}).sort().join(','),
    [survey?.questions]
  );
  const prevQuestionIdsKeyRef = useRef(questionIdsKey);
  useEffect(() => {
    if (prevQuestionIdsKeyRef.current !== questionIdsKey) {
      prevQuestionIdsKeyRef.current = questionIdsKey;
      resetPreviewState();
    }
  }, [questionIdsKey, resetPreviewState]);

  // Si cambia el tipo de layout (0/1/2) o se borran preguntas, el índice
  // del paso actual puede quedar fuera de rango.
  useEffect(() => {
    setCurrentStepIndex((i) => Math.min(i, Math.max(sortedQuestions.length - 1, 0)));
  }, [sortedQuestions.length]);

  useEffect(() => {
    if (showValidation && sortedQuestions.every(([qId, q]) => isQuestionAnswered(q, answers[qId]))) {
      setShowValidation(false);
    }
  }, [answers, showValidation, sortedQuestions]);

  useEffect(() => {
    return () => {
      if (thanksTimeoutRef.current) clearTimeout(thanksTimeoutRef.current);
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
    };
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = useCallback(() => {
    if (!survey) return false;
    return sortedQuestions.every(([qId, q]) => isQuestionAnswered(q, answers[qId]));
  }, [survey, sortedQuestions, answers]);

  // ── Avance automático (layouts isStepped) ──────────────────────────────────
  const scheduleAutoAdvance = useCallback((qId) => {
    if (!isSteppedFlow) return;
    if (!currentQuestionEntry || currentQuestionEntry[0] !== qId) return;
    if (isLastQuestion) return;

    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    autoAdvanceTimeoutRef.current = setTimeout(() => {
      setShowValidation(false);
      setCurrentStepIndex((i) => Math.min(i + 1, sortedQuestions.length - 1));
    }, AUTO_ADVANCE_DELAY_MS);
  }, [isSteppedFlow, currentQuestionEntry, isLastQuestion, sortedQuestions.length]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleRatingChange = useCallback((qId, v) => {
    setAnswers((p) => ({ ...p, [qId]: v }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleSliderChange = useCallback(
    (qId, v) => setAnswers((p) => ({ ...p, [qId]: v })), []
  );

  const handleSliderCommit = useCallback((qId) => {
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleTextChange = useCallback(
    (qId, e) => setAnswers((p) => ({ ...p, [qId]: e.target.value })), []
  );

  const handleRadioChange = useCallback((qId, e) => {
    const value = e.target.value;
    setAnswers((p) => ({ ...p, [qId]: value }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleEmojiChange = useCallback((qId, emoji) => {
    const numericValue = emoji.match(/face(\d+)/)?.[1] ?? emoji;
    setAnswers((p) => ({ ...p, [qId]: numericValue }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleCommentChange = useCallback((e) => {
    if (e.target.value.length <= COMMENT_MAX_LENGTH) setComment(e.target.value);
  }, []);

  const handleStartSurvey = useCallback(() => {
    setSurveyStarted(true);
  }, []);

  // ── Navegación de layouts isStepped ─────────────────────────────────────────
  const handleStepNext = useCallback(() => {
    if (!currentQuestionEntry) return;
    const [qId, q] = currentQuestionEntry;
    if (!isQuestionAnswered(q, answers[qId])) {
      setShowValidation(true);
      setError(t.questionRequired || t.validateAll);
      return;
    }
    setShowValidation(false);
    setCurrentStepIndex((i) => Math.min(i + 1, sortedQuestions.length - 1));
  }, [currentQuestionEntry, answers, sortedQuestions.length, t]);

  const handleGoToStep = useCallback((idx) => {
    if (idx > currentStepIndex) {
      const answered =
        currentQuestionEntry &&
        isQuestionAnswered(currentQuestionEntry[1], answers[currentQuestionEntry[0]]);
      if (!answered) {
        setShowValidation(true);
        setError(t.questionRequired || t.validateAll);
        return;
      }
    }

    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    setShowValidation(false);
    setCurrentStepIndex(idx);
  }, [currentStepIndex, currentQuestionEntry, answers, t]);

  // ── Submit (sin Firestore) ───────────────────────────────────────────────────
  // Arma el mismo `docData` que produciría un envío real (mismo shape:
  // answer/month/anio/etc.) para que ThanksUserResult pueda puntuar y
  // mostrar el resultado tal cual se vería en producción, pero nunca
  // llega a tocar la base de datos.
  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (isSteppedFlow && !isLastQuestion) {
      handleStepNext();
      return;
    }

    if (!validateForm()) {
      setShowValidation(true);
      setError(t.validateAll);
      return;
    }

    setShowValidation(false);
    setSubmitting(true);

    const now     = new Date();
    const year    = now.getFullYear();
    const month   = now.getMonth() + 1;

    const answerMap = {};
    sortedQuestions.forEach(([qId, q]) => {
      const orden = q.orden || 0;
      answerMap[`q${orden}`] = { ans: answers[qId] ?? null, orden };
    });

    const docData = {
      addRec:    now,
      answer:    answerMap,
      month,
      anio:      year,
      createdBy: survey?.createdBy || '',
      uid:       'preview-user',
    };
    if (comment.trim()) docData.comm = comment.trim();

    submitTimeoutRef.current = setTimeout(() => {
      setSubmitting(false);
      setSubmitSuccess(true);
      setSubmittedDocData(docData);
      setPreparingThanks(true);

      thanksTimeoutRef.current = setTimeout(() => {
        setShowThanks(true);
        setPreparingThanks(false);
      }, THANKS_DELAY_MS);
    }, PREVIEW_SUBMIT_DELAY_MS);
  }, [survey, sortedQuestions, answers, comment, validateForm, t, isSteppedFlow, isLastQuestion, handleStepNext]);

  // ── Misc handlers ──────────────────────────────────────────────────────────
  const handleCloseError = useCallback(() => setError(''), []);
  const handleRetake     = useCallback(() => resetPreviewState(), [resetPreviewState]);

  // ── Early returns ──────────────────────────────────────────────────────────
  if (!survey) {
    return (
      <Container maxWidth={false} disableGutters className="main-container">
        <PreviewToolbar onReset={resetPreviewState} onExit={onExitPreview} />
        <Alert severity="info">No hay datos de encuesta para previsualizar.</Alert>
      </Container>
    );
  }

  if (showThanks) {
    return (
      <Container maxWidth={false} disableGutters className="main-container">
        <PreviewToolbar onReset={resetPreviewState} onExit={onExitPreview} />
        {survey.showResult
          ? <ThanksUserResult docData={submittedDocData} resultRules={survey.resultRules || []} />
          : <ThanksUser docData={submittedDocData} />}
      </Container>
    );
  }

  if (preparingThanks) {
    return (
      <Container maxWidth={false} disableGutters className="main-container">
        <PreviewToolbar onReset={resetPreviewState} onExit={onExitPreview} />
        <PreparingResultScreen willShowResult={!!survey.showResult} t={t} />
      </Container>
    );
  }

  if (!surveyStarted) {
    return (
      <Container maxWidth={false} disableGutters className="main-container">
        <PreviewToolbar onReset={resetPreviewState} onExit={onExitPreview} />
        <IntroSurvey
          logoUrl={logoUrl}
          title={survey.title}
          description={survey.description}
          buttonText={t.startSurvey || 'Comenzar ahora'}
          surveyStyle={survey.style}
          onStart={handleStartSurvey}
        />
        <PlatformFooter t={t} />
      </Container>
    );
  }

  const showCommentBlock = survey.showComment && (!isSteppedFlow || isLastQuestion);
  const showCompleteAllHint = (!isSteppedFlow || isLastQuestion) && progress < 100 && !submitting && !submitSuccess;

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Container maxWidth={false} disableGutters className="main-container">
      <PreviewToolbar onReset={resetPreviewState} onExit={onExitPreview} />

      <SubmittingOverlay
        open={submitting}
        message={t.savingAnswers || 'Saving your answers..'}
      />

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" className="alert">{error}</Alert>
      </Snackbar>

      <ProgressBarUser progress={progress} label={t.progress} />

      <div
        className={survey.style ?? 'bg-white'}
        style={{ width: '100%', borderRadius: '8px', overflow: 'hidden' }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate className="survey-form">
          <Paper
            elevation={3}
            className="survey-paper survey-paper-with-sticky-progress"
            sx={{ background: 'transparent' }}
          >
            <FormLayout
              survey={survey}
              sortedQuestions={sortedQuestions}
              answers={answers}
              currentStepIndex={currentStepIndex}
              currentQuestionEntry={currentQuestionEntry}
              isLastQuestion={isLastQuestion}
              unansweredQuestions={unansweredQuestions}
              onRatingChange={handleRatingChange}
              onSliderChange={handleSliderChange}
              onSliderCommit={handleSliderCommit}
              onTextChange={handleTextChange}
              onRadioChange={handleRadioChange}
              onEmojiChange={handleEmojiChange}
              onGoToStep={handleGoToStep}
              t={t}
            />

            {showCommentBlock && (
              <Card
                variant="outlined"
                className="user-info-card"
                sx={{ ...GLASS_SX, borderColor: 'secondary.light' }}
              >
                <CardContent>
                  <Typography variant="h6" className="user-info-title">
                    {t.additionalComments}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder={t.commentsPlaceholder}
                    value={comment}
                    onChange={handleCommentChange}
                    inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
                    className="user-info-textfield"
                    sx={{ mt: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      color={comment.length >= COMMENT_MAX_LENGTH ? 'error' : 'text.secondary'}
                    >
                      {comment.length} / {COMMENT_MAX_LENGTH}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Box
              className={`submit-button-container${isSteppedFlow ? ' stepped-fixed-submit' : ''}`}
            >
              {showNextOrSubmitButton && (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={
                    submitting ||
                    submitSuccess ||
                    (isSteppedFlow
                      ? !isQuestionAnswered(
                          currentQuestionEntry?.[1] || {},
                          answers[currentQuestionEntry?.[0]]
                        )
                      : progress < 100)
                  }
                  endIcon={
                    submitting
                      ? <CircularProgress size={24} color="inherit" />
                      : (isSteppedFlow && !isLastQuestion) ? null : <SendIcon />
                  }
                  className="submit-button"
                >
                  {submitSuccess
                    ? t.submitted
                    : submitting
                    ? t.submitting
                    : (isSteppedFlow && !isLastQuestion)
                    ? (t.nextQuestion || 'Siguiente →')
                    : t.submitButton}
                </Button>
              )}

              {showCompleteAllHint && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                >
                  {t.completeAll}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </div>

      <PlatformFooter t={t} />
    </Container>
  );
};

export default SurveyPreview;